import cx from "classnames";
import { FC, useMemo } from "react";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { MultiMapExportSpecialty } from "@app/projects/network-assessment/multi-map-export/interfaces";
import { formatSpecialtyLabel, formatSpecialtySummary } from "@app/projects/network-assessment/multi-map-export/utils/specialtySelection";

export interface SpecialtiesSelectorProps {
  /** Separates an empty filter from a template with no specialty groups. */
  hasSpecialties: boolean;
  onChange: (codes: string[]) => void;
  selectedSpecialtyGroupCodes: readonly string[];
  /** Only the specialties matching the selected Specialty Type pills. */
  specialtiesInScope: readonly MultiMapExportSpecialty[];
}

export const SpecialtiesSelector: FC<SpecialtiesSelectorProps> = ({
  hasSpecialties,
  onChange,
  selectedSpecialtyGroupCodes,
  specialtiesInScope,
}: SpecialtiesSelectorProps) => {
  const selected = useMemo(() => new Set(selectedSpecialtyGroupCodes), [selectedSpecialtyGroupCodes]);
  const selectedInView = specialtiesInScope.filter((specialty) => selected.has(specialty.code));
  const allInViewSelected = specialtiesInScope.length > 0 && selectedInView.length === specialtiesInScope.length;

  const setSpecialtiesSelected = (codes: readonly string[], isSelected: boolean) => {
    const changed = new Set(codes);

    onChange(isSelected
      ? [...selectedSpecialtyGroupCodes, ...codes.filter((code) => !selected.has(code))]
      : selectedSpecialtyGroupCodes.filter((code) => !changed.has(code)));
  };

  // Keyed off hasSpecialties, not what is in scope, so clearing every Specialty Type pill still
  // reports the empty required selection rather than silently showing an empty list.
  const showRequiredError = hasSpecialties && selectedInView.length === 0;

  return (
    <div className="multi-map-export__field">
      <span className="multi-map-export__field-label" data-testid="multi-map-export__specialties-label">
        Specialties<span className="text-danger"> *</span>
      </span>
      <div
        className={cx({
          "multi-map-export__specialties": true,
          "multi-map-export__specialties--error": showRequiredError,
        })}
        data-testid="multi-map-export__specialties"
      >
        <div className="multi-map-export__specialties-summary-bar">
          {specialtiesInScope.length > 0 && (
            <QESCheckbox
              checked={allInViewSelected}
              containerClassName="multi-map-export__specialties-select-all"
              indeterminate={selectedInView.length > 0 && !allInViewSelected}
              label="Select All"
              onChange={(event) => setSpecialtiesSelected(specialtiesInScope.map((specialty) => specialty.code), event.target.checked)}
              testId="multi-map-export__specialties-select-all"
            />
          )}
          <span className="multi-map-export__specialties-summary" data-testid="multi-map-export__specialties-summary">
            {formatSpecialtySummary(specialtiesInScope)}
          </span>
        </div>
        {specialtiesInScope.length === 0 ? (
          <p className="multi-map-export__specialties-empty" data-testid="multi-map-export__specialties-empty">
            {hasSpecialties ? "No specialties match the selected types." : "This project has no specialties."}
          </p>
        ) : (
          <ul aria-label="Specialties" className="multi-map-export__specialties-list">
            {specialtiesInScope.map((specialty) => (
              <li className="multi-map-export__specialties-row" key={specialty.code}>
                <QESCheckbox
                  checked={selected.has(specialty.code)}
                  containerClassName="multi-map-export__specialties-checkbox"
                  label={formatSpecialtyLabel(specialty)}
                  onChange={(event) => setSpecialtiesSelected([specialty.code], event.target.checked)}
                  testId={`multi-map-export__specialties-specialty--${specialty.code}`}
                />
                <span className="multi-map-export__specialties-row-meta">{specialty.specialtyType}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {showRequiredError && (
        <p className="multi-map-export__field-error" data-testid="multi-map-export__specialties-error">Select at least one specialty.</p>
      )}
    </div>
  );
};

export default SpecialtiesSelector;
